import { create } from 'zustand';
import apiClient from '../api/apiClient';

export interface Workrole {
    id: number;
    role_id: string;
    name: string;
    status: boolean;
    description?: string | null;
    job_category?: string | null;
    job_group?: string | null;
    employee_count?: string;
}

interface WorkroleState {
    workroles: Workrole[];
    isLoading: boolean;
    error: string | null;
    hasFetchedAll: boolean;

    fetchWorkroles: (opts?: { force?: boolean }) => Promise<Workrole[]>;
    fetchActiveWorkroles: (opts?: { force?: boolean }) => Promise<Workrole[]>;
    reset: () => void;
}

const sortWorkroles = (workroles: Workrole[]): Workrole[] =>
    [...workroles].sort((a, b) => {
        // Sort by status (active first), then by name
        if (a.status !== b.status) {
            return a.status ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
    });

export const useWorkroleStore = create<WorkroleState>((set, get) => ({
    workroles: [],
    isLoading: false,
    error: null,
    hasFetchedAll: false,

    async fetchWorkroles(opts) {
        const force = opts?.force ?? false;
        if (!force && get().hasFetchedAll) {
            if (get().isLoading) {
                set({ isLoading: false });
            }
            return get().workroles;
        }

        set({ isLoading: true, error: null });

        try {
            const response = await apiClient.get('/admin/workrole/');
            const workroleData = response.data?.data || [];
            const sorted = sortWorkroles(Array.isArray(workroleData) ? workroleData : []);

            set({
                workroles: sorted,
                isLoading: false,
                error: null,
                hasFetchedAll: true,
            });

            return sorted;
        } catch (error) {
            console.error('Failed to fetch workroles', error);
            const message = error instanceof Error ? error.message : 'Unable to load workroles';
            set({ error: message, isLoading: false });
            throw error;
        }
    },

    async fetchActiveWorkroles(opts) {
        set({ isLoading: true, error: null });

        try {
            const response = await apiClient.get('/admin/workrole/');
            const workroleData = response.data?.data || [];
            // Filter for active workroles
            const activeWorkroles = (Array.isArray(workroleData) ? workroleData : []).filter(wr => wr.status === true);
            const sorted = sortWorkroles(activeWorkroles);

            set({
                workroles: sorted,
                isLoading: false,
                error: null,
            });

            return sorted;
        } catch (error) {
            console.error('Failed to fetch active workroles', error);
            const message = error instanceof Error ? error.message : 'Unable to load active workroles';
            set({ error: message, isLoading: false });
            throw error;
        }
    },

    reset() {
        set({
            workroles: [],
            isLoading: false,
            error: null,
            hasFetchedAll: false,
        });
    },
}));
